import { capitalCase } from 'change-case';
import { utc, UTC, guid, DateRange, isNullOrUndefined, LQ } from 'rewire-common';
import { observable, createWatcher, WatcherType } from 'rewire-core';
import {
  ICell,
  IColumn,
  IGrid,
  IGridOptions,
  IRow,
  IRowData,
  createColumn,
  createGrid,
  // ColumnModel,
  // IRowProps
} from 'rewire-grid';
import {
  validator,
  ErrorSeverity,
  IError,
  IValidationFn,
  // validators,
} from 'rewire-ui';
import { numberColumn, IMessage, IOtherPayment, IEmployeeSummary, FieldFilter } from '../DataTypes';
import { makeFriendlyRange, makeShortCellDate } from '../utility/DateUtils';
import { getNameSearcher } from '../utility/Searchers';
import { capitalize } from '../utility/StringUtils';
import { DeleteIconRenderer } from './CellRenderers';
import timecardModel, { TimecardModelBase } from './TimecardModel';
import { IRolledUpTimecard } from './DailyRollupModel';

const reasonFilter: FieldFilter = {
  field: 'category',
  eq: 'other',
};

const reasonSearcher = getNameSearcher('reasons', () => reasonFilter);

interface IOtherPaymentsState {
  grid: IGrid;
  expanded: boolean;
}

export abstract class OtherPaymentsModelBase {
  state: IOtherPaymentsState;
  watchOtherPayments: WatcherType<IOtherPayment[]>;

  constructor() {
    this.state = observable({
      grid: {} as IGrid,
      expanded: true,
    });
    this.grid = this.createGrid([]);
    this.watchOtherPayments = createWatcher();
  }

  abstract get timecardModel(): TimecardModelBase;

  get effective(): DateRange | undefined {
    return this.timecardModel.effective;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return this.timecardModel.selectedEmployee;
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  get expanded(): boolean {
    return this.state.expanded;
  }

  set expanded(expanded: boolean) {
    this.state.expanded = expanded;
  }

  get canEditTimecard(): boolean {
    return true;
    // return this.timecardModel.canEditTimecard && this.timecardModel.rollupModel.rolledUpTimecards.some(rt => rt.canEditTimecard);
  }

  private handleRecalculateAmount = (cell: ICell, _value: any) => {
    const otherPayment = cell.row.data;
    this.timecardModel.recalculateOtherPaymentAmount(otherPayment);
  };

  private handleDateChanged = (cell: ICell, value: any) => {
    const otherPayment: IOtherPayment = cell.row.data;
    otherPayment.earnedDate = utc(value);
  };

  private deleteRow = (cell: ICell) => {
    const row = cell.row;
    if (!this.rowIsDeletable(row)) return;
    const otherPayment = row.data;
    this.timecardModel.deleteOtherPayment(otherPayment);
  };

  rowIsDeletable(row: IRow): boolean {
    return this.rowIsEditable(row) && !this.isDataEntryRow(row);
  }

  private isDataEntryRow(row: IRow): boolean {
    return this.canEditTimecard && row.position === row.grid.rows.length - 1 && !timecardModel.isCompleteOtherPayment(row.data);
  }

  private isNewRow(row: IRow): boolean {
    return timecardModel.otherPaymentTracker.rowByIdHasChanges(row.data.id);
  }

  private getRollupForDate(date: UTC): IRolledUpTimecard | undefined {
    return LQ(this.timecardModel.rollupModel.getTimecards())
      .filter((rt) => rt.earnedDate.equals(date))
      .first();
  }

  private rollupForDateIsEditable(date: UTC): boolean {
    const rollupForDate = this.getRollupForDate(date);
    return rollupForDate?.canEditTimecard ?? true;
  }

  private rowIsEditable(row: IRow): boolean {
    if (!this.canEditTimecard) return false;
    if (this.isNewRow(row)) return true;
    const earnedDate = row.data.earnedDate;
    return isNullOrUndefined(earnedDate) || this.rollupForDateIsEditable(earnedDate);
  }

  getValidateDateFn(): IValidationFn {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const model = this;
    return function (this: IRow, value: UTC): IError | undefined {
      let errorMsg: string = '';

      if (isNullOrUndefined(value)) {
        errorMsg = 'is required';
      } else if (!model.effective!.inRange(value)) {
        errorMsg = `must be in the range ${makeFriendlyRange(model.effective!)}`;
      } else if (model.rowIsEditable(this) && !model.rollupForDateIsEditable(value)) {
        errorMsg = `must be for a date not in a closed batch`;
      }

      return errorMsg ? { text: errorMsg, severity: ErrorSeverity.Error } : undefined;
    };
  }

  private handleError = (row: IRow, field: string, error: IError | undefined) => {
    const otherPayment: IOtherPayment = row.data;

    // if there is an error but we're currently evaluating the data entry row, return
    if (this.isDataEntryRow(row)) {
      row.cells[field].error = undefined;
      return;
    }

    // however, if it's a 'complete' other payment, we may be adding or removing an error on the timecardModel
    const earnedDate = otherPayment.earnedDate;
    const message: IMessage = {
      id: `${otherPayment.id}${field}`, // ${earnedDate}
      level: 'info',
      effective: earnedDate,
      text: error && `${capitalize(capitalCase(field))} ${error.text}`,
    };
    if (error) {
      this.timecardModel.addMessage(message);
    } else {
      this.timecardModel.removeMessage(message);
    }
  };

  private get columns(): any[] {
    const columns = [
      { name: 'id', label: 'Id', visible: false },
      {
        name: 'hideDeleteIcon',
        label: '',
        type: { type: 'text', options: { onClick: (cell: ICell) => () => this.deleteRow(cell) } },
        renderer: DeleteIconRenderer,
        width: '35px',
        align: 'center',
        enabled: false,
      },
      {
        name: 'earnedDate',
        label: 'Date',
        type: 'date',
        map: makeShortCellDate,
        validators: this.getValidateDateFn(),
        onValueChange: this.handleDateChanged,
      },
      {
        name: 'units',
        label: 'Units',
        type: { type: 'number', options: { fixed: true, decimals: 2, thousandSeparator: true } },
        validators: ['required', validator('>', 0)],
        onValueChange: this.handleRecalculateAmount,
      },
      {
        name: 'rate',
        label: 'Rate',
        type: { type: 'number', options: { fixed: true, decimals: 4, thousandSeparator: true } },
        validators: 'required',
        onValueChange: this.handleRecalculateAmount,
      },
      {
        name: 'factor',
        label: 'Factor',
        type: { type: 'number', options: { fixed: true, decimals: 4, thousandSeparator: true } },
        validators: 'required',
        onValueChange: this.handleRecalculateAmount,
      },
      { name: 'amount', label: 'Amount', type: numberColumn, enabled: false },
      { name: 'reason', label: 'Reason', type: { type: 'auto-complete', options: reasonSearcher }, validators: 'required' },
      { name: 'accountCode', label: 'Account Code', type: 'text' },
    ];

    columns.forEach((col: any) => {
      col.onValueChange = this.compositeColumnOnValueChange(col.onValueChange);
    });

    return columns;
  }

  private compositeColumnOnValueChange(onValueChange?: (cell: ICell, value: any) => void): (cell: ICell, value: any) => void {
    if (onValueChange) {
      return (cell: ICell, value: any) => {
        onValueChange(cell, value);
        this.autoAddNewDataEntryRow(cell);
      };
    } else {
      return (cell: ICell, _value: any) => {
        this.autoAddNewDataEntryRow(cell);
      };
    }
  }

  private autoAddNewDataEntryRow(cell: ICell) {
    const row = cell.row;
    if (row.position < cell.grid.rows.length - 1) {
      return;
    }

    const dataEntryOtherPayment = this.timecardModel.createDataEntryOtherPaymentIfNecessary(
      this.timecardModel.timecards,
      this.timecardModel.otherPayments,
    );
    if (dataEntryOtherPayment) {
      this.timecardModel.otherPayments.push(dataEntryOtherPayment);
    }
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    this.columns.forEach((col) => {
      const { name, label, ...opts } = col;
      opts.canSort = false;
      const column = createColumn(name, label, opts);
      cols.push(column);
    });
    return cols;
  }

  private createRows(rowData: any[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (rowData) {
      rowData.forEach((row) => {
        const r: IRowData = { id: `${guid()}`, data: row, options: row.options };
        rows.push(r);
      });
    }
    return rows;
  }

  reset() {
    this.grid.set([]);
  }

  load() {
    const currentCell = this.grid.focusedCell;
    const selectedRowId = currentCell?.row.data.id;
    const selectedCellName = currentCell?.column.name;
    this.grid.readOnly = !this.canEditTimecard;
    const otherPayments: IOtherPayment[] = this.timecardModel.otherPayments;
    const rows: IRowData[] = this.createRows(otherPayments, this.grid.columns);
    this.grid.set(rows);
    this.grid.rows.forEach((row) => {
      if (!this.rowIsEditable(row)) {
        Object.keys(row.cells).forEach((cellKey) => {
          row.cells[cellKey].readOnly = true;
        });
      }
    });

    if (otherPayments) {
      this.watchOtherPayments(otherPayments, ['length'], () => {
        setTimeout(() => this.load(), 0);
      });
    }

    if (selectedRowId) {
      const selectedRow = this.grid.rows.find((r) => r.data.id === selectedRowId);
      if (selectedRow) {
        this.grid.selectRows([selectedRow]);
        if (selectedCellName) {
          const cellToSelect = selectedRow.cells[selectedCellName];
          if (cellToSelect) {
            if (currentCell) {
              this.grid.selectCells([cellToSelect]);
            }
          }
        }
      }
    }
  }

  private createOptions(): IGridOptions {
    return {
      clearSelectionOnBlur: false,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (evt: React.KeyboardEvent<any>, cell: ICell) => this.deleteRowKeybind(evt, cell),
      },
    };
  }

  private deleteRowKeybind = (evt: React.KeyboardEvent<any>, cell: ICell) => {
    if (cell.editing) return;
    this.deleteRow(cell);
  };

  private createGrid(results: IOtherPayment[]): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(results, cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 32;
    grid.onError = this.handleError;
    return grid;
  }
}

export class OtherPaymentsModel extends OtherPaymentsModelBase {
  constructor() {
    super();
  }

  get timecardModel(): TimecardModelBase {
    return timecardModel;
  }
}

const otherPaymentsModel = new OtherPaymentsModel();
export default otherPaymentsModel;
